// src/cockpit4/components/_MonitorEmptyState.tsx
// 统一的"空态纸飞机开箱"插图（紫青深色玻璃风，与 GEO+DSO 其他监测面板一致）
import { Package } from "lucide-react";

export default function _MonitorEmptyState({ text = "暂无数据" }: { text?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 select-none">
      <div className="relative w-[100px] h-[100px]">
        {/* 底部阴影 */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-3 rounded-full bg-indigo-400/30 blur-[4px]" />
        {/* 箱子 主体 */}
        <svg viewBox="0 0 120 120" className="w-[100px] h-[100px] drop-shadow-lg">
          <defs>
            <linearGradient id="boxGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0.98" />
              <stop offset="100%" stopColor="#818cf8" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="boxGradSide" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0.85" />
            </linearGradient>
            <linearGradient id="tapeGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          {/* 箱底 */}
          <path d="M20 70 L60 95 L100 70 L100 100 L60 115 L20 100 Z" fill="url(#boxGradSide)" stroke="#22d3ee" strokeOpacity="0.5" strokeWidth="1.2" />
          {/* 箱前 */}
          <path d="M20 55 L60 80 L100 55 L100 70 L60 95 L20 70 Z" fill="url(#boxGrad)" stroke="#22d3ee" strokeOpacity="0.4" strokeWidth="1.2" />
          {/* 箱盖（左 张开） */}
          <path d="M20 55 L60 45 L60 80 L20 70 Z" fill="#7c6bff" stroke="#22d3ee" strokeOpacity="0.5" strokeWidth="1.2" />
          {/* 箱盖（右 张开 更大） */}
          <path d="M100 55 L80 20 L60 45 L60 80 L100 70 Z" fill="#a5b4fc" stroke="#22d3ee" strokeOpacity="0.55" strokeWidth="1.2" />
          {/* 封箱带 */}
          <path d="M55 48 Q52 20 40 10" stroke="url(#tapeGrad)" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeDasharray="0.5 4" />
          {/* 标签圆 */}
          <circle cx="85" cy="80" r="5.5" fill="none" stroke="#22d3ee" strokeWidth="1.4" />
          <circle cx="85" cy="80" r="2.2" fill="#22d3ee" />
          <circle cx="38" cy="70" r="4" fill="none" stroke="#8b5cf6" strokeWidth="1.2" strokeDasharray="2 2" />
        </svg>
        {/* 纸飞机 */}
        <svg viewBox="0 0 40 40" className="absolute -top-2 right-0 w-9 h-9 drop-shadow animate-pulse">
          <defs>
            <linearGradient id="planeGrad2" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
          </defs>
          <path d="M34 4 L5 20 L17 22 L34 4 Z" fill="url(#planeGrad2)" />
          <path d="M34 4 L17 22 L20 30 L23 23 L34 4 Z" fill="#6366f1" fillOpacity="0.85" />
        </svg>
        {/* 虚线轨迹 */}
        <svg viewBox="0 0 120 120" className="absolute inset-0 w-full h-full pointer-events-none">
          <path d="M30 28 Q50 12 80 10" stroke="#22d3ee" strokeOpacity="0.65" strokeWidth="1.5" strokeDasharray="3 4" fill="none" strokeLinecap="round" />
        </svg>
      </div>
      <div className="mt-2 text-[12px] text-gray-500">{text}</div>
    </div>
  );
}

/** 过滤器按钮 + 工具栏（共享 —— 深色玻璃风）  */
export function MonitorFilterBar({
  onAddBrand,
  platformText = "全部13个平台",
  questionText = "全部0个问题",
  dateStart, dateEnd, onDateChange,
  children,
}: {
  onAddBrand: () => void;
  platformText?: string;
  questionText?: string;
  dateStart: string;
  dateEnd: string;
  onDateChange?: (s: string, e: string) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap px-4 py-3">
      <button onClick={onAddBrand}
        className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-[13px]
                   bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white hover:border-indigo-400/50 hover:bg-indigo-500/10 transition-colors min-w-[220px]">
        <span className="w-3 h-3 rounded border border-gray-500 flex items-center justify-center">
          <span className="w-1.5 h-px bg-gray-400" />
        </span>
        增加品牌监测
      </button>
      <FilterSelect text={platformText} Icon={<Package size={11} />} />
      <FilterSelect text={questionText} />
      <div className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-gray-400 text-[13px]">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <input type="date" value={dateStart}
          onChange={(e) => onDateChange?.(e.target.value, dateEnd)}
          className="bg-transparent outline-none text-gray-300 font-mono [color-scheme:dark]" />
        <span className="text-gray-500 mx-0.5">至</span>
        <input type="date" value={dateEnd}
          onChange={(e) => onDateChange?.(dateStart, e.target.value)}
          className="bg-transparent outline-none text-gray-300 font-mono [color-scheme:dark]" />
      </div>
      {children}
    </div>
  );
}

function FilterSelect({ text, Icon }: { text: string; Icon?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-gray-400 text-[13px] min-w-[240px] justify-between cursor-pointer hover:border-indigo-400/50 hover:bg-indigo-500/10 hover:text-white">
      <div className="flex items-center gap-2">
        {Icon}
        <span>{text}</span>
      </div>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

/** 紫青色 分节标题（深色玻璃风） */
export function SectionHeader({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="relative h-9 items-center flex mb-3">
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[18px] rounded bg-gradient-to-r from-violet-500/20 via-cyan-500/10 to-transparent" />
      <div className="relative flex items-center gap-1.5 ml-1 text-[14px] font-bold text-white">
        <span className="w-1.5 h-4 rounded bg-gradient-to-b from-violet-400 to-cyan-400" />
        {label}
      </div>
      {children && <div className="relative ml-auto">{children}</div>}
    </div>
  );
}
